"use client";

import { useTranslations } from "next-intl";
import { ForgotPasswordContext } from "@/context";
import { validatePassword } from "@/utils/utils";
import { resetPassword } from "@/api/user/resetPassword";
import { useMutation } from "@tanstack/react-query";
import useToast from "@/hooks/useToast";
import {
  LockClockOutlined,
  CheckCircleRounded as CheckIcon,
  KeyRounded as KeyIcon,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  Card,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useState } from "react";
import { useRouter } from "next/navigation"; 
import { useLocale } from "next-intl";

export const CreateNewPassword = ({
  password,
  setPassword,
  mobile,
}) => {
  const t = useTranslations();
  const { showToast } = useToast();
  const { step, setStep } = useContext(ForgotPasswordContext);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState("");
  const locale = useLocale();
  const router = useRouter();

  const {
    isLengthValid,
    hasNumber,
    hasUpperCase,
    hasSpecialChar,
    confirmPasswordValid,
  } = validatePassword(password, confirmPassword);

  const mutation = useMutation({
    mutationFn: ({ mobile, password }) =>
      resetPassword({ mobile_number: mobile, password }),
    onSuccess: () => {
      showToast({
        type: "success",
        message: "Password reset successful! Redirecting...",
      });
      router.push(`/${locale}/login`);
    },
    onError: (err) => {
      const message = err?.message || t("Password reset failed");
      showToast({ type: "error", message });
      setResetPasswordError(message);
      setResetPasswordSuccess("");
    },
  });

  const handleClickResetPassword = () => {
    if (!isLengthValid || !hasNumber || !hasUpperCase || !hasSpecialChar) {
      showToast({
        type: "error",
        message: t("Password does not meet the required criteria."),
      });
      return;
    }

    if (password !== confirmPassword) {
      showToast({ type: "error", message: t("Passwords do not match.") });
      return;
    }

    mutation.mutate({ mobile, password });
  };

  return (
    <Card
      sx={{ gap: "1.5rem", maxWidth: "25rem", margin: "auto" }}
      elevation={1}
    >
      <Alert severity="success">
        {t("Identity verification successful Please set your new password")}
      </Alert>

      <Typography variant="h4">{t("Create New Password")}</Typography>
      <Typography color="light-grey" variant="body1">
        {t("Choose a strong password that you havent used before")}
      </Typography>

      <div className="w-full flex flex-col gap-6">
        <div className="w-full flex flex-col gap-1">
          <Typography variant="body1">{t("New Password")}</Typography>
          <TextField
            placeholder={t("Enter new password")}
            variant="outlined"
            type="password"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockClockOutlined />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className="w-full flex flex-col gap-1">
          <Typography variant="body1">{t("Confirm New Password")}</Typography>
          <TextField
            placeholder={t("Confirm new password")}
            variant="outlined"
            type="password"
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockClockOutlined />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <Card variant="grey">
          <Typography variant="body1">{t("Password Requirements")}</Typography>
          <Typography color="grey" variant="body1">
            {isLengthValid ? <CheckIcon color="primary" /> : "❌"} {t("8 characters")}
          </Typography>
          <Typography color="grey" variant="body1">
            {hasUpperCase ? <CheckIcon color="primary" /> : "❌"}{" "}
            {t("At least one uppercase letter")}
          </Typography>
          <Typography color="grey" variant="body1">
            {hasNumber ? <CheckIcon color="primary" /> : "❌"}{" "}
            {t("At least one number")}
          </Typography>
          <Typography color="grey" variant="body1">
            {hasSpecialChar ? <CheckIcon color="primary" /> : "❌"}{" "}
            {t("At least one special character")}
          </Typography>
          {confirmPassword && (
            <Typography color="grey" variant="body1">
              {confirmPasswordValid ? (
                <CheckIcon color="primary" />
              ) : (
                "❌"
              )}{" "}
              {confirmPasswordValid
                ? t("Passwords match")
                : t("Passwords do not match")}
            </Typography>
          )}
        </Card>

        {resetPasswordError && (
          <Alert color="error">{resetPasswordError}</Alert>
        )}
        {resetPasswordSuccess && (
          <Alert severity="success">{resetPasswordSuccess}</Alert>
        )}

        <Button
          variant="contained"
          onClick={handleClickResetPassword}
          fullWidth
          endIcon={<KeyIcon />}
          disabled={mutation.isLoading}
        >
          {mutation.isLoading ? t("Resetting...") : t("Reset Password")}
        </Button>
      </div>
    </Card>
  );
};

export default CreateNewPassword;
